import * as XLSX from 'xlsx';

class ImportacionCompensacionesService {
  constructor() {
    this.configuracionDefecto = {
      columnas: {
        empleado: ['empleado', 'nombre', 'trabajador', 'employee'],
        cargo: ['cargo', 'puesto', 'position', 'job'],
        area: ['área', 'area', 'área de trabajo', 'department'],
        proyecto: ['proyecto', 'project'],
        horasProximoMes: ['total próximo mes', 'horas próximo mes', 'total próximo mes (h)', 'horas'],
        estado: ['estado', 'status'],
        diasLaborables: ['días laborables', 'dias laborables', 'working days'],
        minutosAcum: ['minutos acum', 'minutos acumulados', 'accumulated minutes'],
        tareosProcesados: ['tareos procesados', 'processed tareos']
      },
      validaciones: {
        empleadoRequerido: true,
        horasRequeridas: true,
        horasMinimas: 0,
        horasMaximas: 500
      }
    };
  }

  /**
   * 📂 Procesar archivo y extraer datos de compensaciones
   */
  async procesarArchivo(archivo, configuracion = {}) {
    try {
      const config = { ...this.configuracionDefecto, ...configuracion };
      
      // Validar archivo
      this.validarArchivo(archivo);
      
      // Leer contenido del archivo
      const datosRaw = await this.leerArchivo(archivo);
      
      // Validar estructura
      this.validarEstructura(datosRaw, config);
      
      // Procesar y limpiar datos
      const datosLimpios = this.procesarDatos(datosRaw, config);
      
      // Generar resumen
      const resumen = this.generarResumen(datosLimpios);
      
      return {
        success: true,
        datos: datosLimpios,
        resumen,
        archivo: {
          nombre: archivo.name,
          tamaño: archivo.size,
          fechaProcesamiento: new Date().toISOString()
        }
      };
      
    } catch (error) {
      console.error('Error procesando archivo de compensaciones:', error);
      return {
        success: false,
        error: error.message,
        datos: [],
        resumen: null
      };
    }
  }

  /**
   * 🔍 Validar tipo y tamaño de archivo
   */
  validarArchivo(archivo) {
    if (!archivo) {
      throw new Error('No se ha seleccionado ningún archivo');
    }

    const tiposPermitidos = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
      'application/csv'
    ];

    const extensionesPermitidas = /\.(xlsx|xls|csv)$/i;
    
    if (!tiposPermitidos.includes(archivo.type) && !extensionesPermitidas.test(archivo.name)) {
      throw new Error('Formato de archivo no válido. Use Excel (.xlsx, .xls) o CSV (.csv)');
    }

    // Límite de 10MB
    const maxTamaño = 10 * 1024 * 1024;
    if (archivo.size > maxTamaño) {
      throw new Error('El archivo es demasiado grande. Máximo 10MB permitido');
    }
  }

  /**
   * 📖 Leer contenido del archivo
   */
  async leerArchivo(archivo) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (evento) => {
        try {
          let datos;
          
          if (archivo.name.toLowerCase().endsWith('.csv')) {
            datos = this.procesarCSV(evento.target.result);
          } else {
            datos = this.procesarExcel(evento.target.result);
          }
          
          resolve(datos);
        } catch (error) {
          reject(new Error(`Error leyendo el archivo: ${error.message}`));
        }
      };
      
      reader.onerror = () => reject(new Error('Error al leer el archivo'));
      
      if (archivo.name.toLowerCase().endsWith('.csv')) {
        reader.readAsText(archivo, 'UTF-8');
      } else {
        reader.readAsBinaryString(archivo);
      }
    });
  }

  /**
   * 📄 Procesar archivo CSV
   */
  procesarCSV(contenido) {
    const lineas = contenido.split('\n')
      .map(linea => linea.trim())
      .filter(linea => linea);
    
    if (lineas.length < 2) {
      throw new Error('El archivo CSV debe tener al menos encabezados y una fila de datos');
    }
    
    // Detectar separador (coma o punto y coma)
    const separador = this.detectarSeparadorCSV(lineas[0]);
    
    const encabezados = this.parsearFilaCSV(lineas[0], separador);
    const datos = [];
    
    for (let i = 1; i < lineas.length; i++) {
      const valores = this.parsearFilaCSV(lineas[i], separador);
      const fila = {};
      
      encabezados.forEach((encabezado, index) => {
        fila[encabezado] = valores[index] || '';
      });
      
      datos.push(fila);
    }
    
    return datos;
  }

  /**
   * 🔍 Detectar separador CSV
   */
  detectarSeparadorCSV(primeraLinea) {
    const comas = (primeraLinea.match(/,/g) || []).length;
    const puntosComa = (primeraLinea.match(/;/g) || []).length;
    
    return puntosComa > comas ? ';' : ',';
  }

  /**
   * 📝 Parsear fila CSV considerando comillas
   */
  parsearFilaCSV(linea, separador) {
    const resultado = [];
    let dentroComillas = false;
    let valorActual = '';
    
    for (let i = 0; i < linea.length; i++) {
      const char = linea[i];
      
      if (char === '"') {
        dentroComillas = !dentroComillas;
      } else if (char === separador && !dentroComillas) {
        resultado.push(valorActual.trim());
        valorActual = '';
      } else {
        valorActual += char;
      }
    }
    
    resultado.push(valorActual.trim());
    return resultado;
  }

  /**
   * 📊 Procesar archivo Excel
   */
  procesarExcel(contenidoBinario) {
    const workbook = XLSX.read(contenidoBinario, { type: 'binary' });
    
    if (!workbook.SheetNames.length) {
      throw new Error('El archivo Excel no contiene hojas de cálculo');
    }
    
    const primeraHoja = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[primeraHoja];
    
    const datos = XLSX.utils.sheet_to_json(worksheet, {
      header: 1,
      defval: '',
      blankrows: false
    });
    
    if (datos.length < 2) {
      throw new Error('La hoja de Excel debe tener al menos encabezados y una fila de datos');
    }
    
    // Convertir a objeto con encabezados
    const encabezados = datos[0];
    const datosObjeto = datos.slice(1).map(fila => {
      const objeto = {};
      encabezados.forEach((encabezado, index) => {
        objeto[encabezado] = fila[index] || '';
      });
      return objeto;
    });
    
    return datosObjeto;
  }

  /**
   * ✅ Validar estructura de datos
   */
  validarEstructura(datos, configuracion) {
    if (!Array.isArray(datos) || datos.length === 0) {
      throw new Error('No se encontraron datos válidos en el archivo');
    }

    const primerFila = datos[0];
    const columnasDisponibles = Object.keys(primerFila);
    
    // Verificar columna de empleado
    const columnaEmpleado = this.buscarColumna(columnasDisponibles, configuracion.columnas.empleado);
    if (!columnaEmpleado) {
      throw new Error(`No se encontró la columna de empleado. Buscar: ${configuracion.columnas.empleado.join(', ')}`);
    }
    
    // Verificar columna de horas
    const columnaHoras = this.buscarColumna(columnasDisponibles, configuracion.columnas.horasProximoMes);
    if (!columnaHoras) {
      throw new Error(`No se encontró la columna de horas. Buscar: ${configuracion.columnas.horasProximoMes.join(', ')}`);
    }
  }

  /**
   * 🔍 Buscar columna por nombres alternativos
   */
  buscarColumna(columnasDisponibles, nombresAlternativos) {
    for (const nombre of nombresAlternativos) {
      const columnaEncontrada = columnasDisponibles.find(col => 
        col.toLowerCase().includes(nombre.toLowerCase()) ||
        nombre.toLowerCase().includes(col.toLowerCase())
      );
      
      if (columnaEncontrada) {
        return columnaEncontrada;
      }
    }
    return null;
  }

  /**
   * 🔄 Procesar y limpiar datos
   */
  procesarDatos(datosRaw, configuracion) {
    const datosLimpios = [];
    const errores = [];
    
    datosRaw.forEach((fila, index) => {
      try {
        const empleadoProcesado = this.procesarEmpleado(fila, configuracion, index + 1);
        
        if (empleadoProcesado.esValido) {
          datosLimpios.push(empleadoProcesado);
        } else {
          errores.push({
            fila: index + 1,
            errores: empleadoProcesado.errores
          });
        }
        
      } catch (error) {
        errores.push({
          fila: index + 1,
          errores: [error.message]
        });
      }
    });
    
    if (errores.length > 0) {
      console.warn('Errores en procesamiento:', errores);
    }
    
    if (datosLimpios.length === 0) {
      throw new Error('No se pudieron procesar datos válidos del archivo');
    }
    
    return datosLimpios;
  }

  /**
   * 👤 Procesar datos de un empleado individual
   */
  procesarEmpleado(fila, configuracion, numeroFila) {
    const errores = [];
    
    // Extraer datos básicos
    const empleado = this.extraerValor(fila, configuracion.columnas.empleado);
    const cargo = this.extraerValor(fila, configuracion.columnas.cargo);
    const area = this.extraerValor(fila, configuracion.columnas.area);
    const proyecto = this.extraerValor(fila, configuracion.columnas.proyecto);
    const horasTexto = this.extraerValor(fila, configuracion.columnas.horasProximoMes);
    const estado = this.extraerValor(fila, configuracion.columnas.estado) || 'A FAVOR';
    const diasLaborables = this.extraerValor(fila, configuracion.columnas.diasLaborables);
    const minutosAcum = this.extraerValor(fila, configuracion.columnas.minutosAcum);
    const tareosProcesados = this.extraerValor(fila, configuracion.columnas.tareosProcesados);
    
    // Validaciones
    if (!empleado || empleado.trim() === '') {
      errores.push('Nombre de empleado requerido');
    }
    
    const horas = this.parsearHoras(horasTexto);
    if (isNaN(horas)) {
      errores.push('Horas del próximo mes debe ser un número válido');
    } else if (horas < configuracion.validaciones.horasMinimas) {
      errores.push(`Horas no pueden ser menores a ${configuracion.validaciones.horasMinimas}`);
    } else if (horas > configuracion.validaciones.horasMaximas) {
      errores.push(`Horas no pueden ser mayores a ${configuracion.validaciones.horasMaximas}`);
    }
    
    return {
      numeroFila,
      empleado: empleado?.trim() || '',
      cargo: cargo?.trim() || '',
      areaTrabjao: area?.trim() || '',
      proyecto: proyecto?.trim() || '',
      horasProximoMes: horas,
      estado: estado?.trim() || 'A FAVOR',
      diasLaborables: parseInt(diasLaborables) || 0,
      minutosAcumulados: parseInt(minutosAcum) || 0,
      tareosProcesados: parseInt(tareosProcesados) || 0,
      horasCalculadas: this.calcularHorasDesdeMinutos(parseInt(minutosAcum) || 0),
      esValido: errores.length === 0,
      errores: errores,
      fechaProcesamiento: new Date().toISOString()
    };
  }

  /**
   * 🔍 Extraer valor de fila usando nombres alternativos
   */
  extraerValor(fila, nombresAlternativos) {
    const columna = this.buscarColumna(Object.keys(fila), nombresAlternativos);
    return columna ? String(fila[columna]).trim() : '';
  }

  /**
   * 🔢 Parsear horas desde texto
   */
  parsearHoras(textoHoras) {
    if (!textoHoras) return 0;
    
    const texto = String(textoHoras).trim();
    
    // Remover caracteres no numéricos excepto punto y coma
    const numeroLimpio = texto.replace(/[^\d.,]/g, '');
    
    // Reemplazar coma por punto para decimales
    const numeroFinal = numeroLimpio.replace(',', '.');
    
    return parseFloat(numeroFinal) || 0;
  }

  /**
   * ⏰ Calcular formato horas:minutos desde minutos totales
   */
  calcularHorasDesdeMinutos(minutosTotales) {
    const horas = Math.floor(minutosTotales / 60);
    const minutos = minutosTotales % 60;
    return `${horas}:${minutos.toString().padStart(2, '0')}`;
  }

  /**
   * 📊 Generar resumen de datos procesados
   */
  generarResumen(datos) {
    const totalRegistros = datos.length;
    const totalHoras = datos.reduce((sum, item) => sum + item.horasProximoMes, 0);
    const empleadosUnicos = [...new Set(datos.map(item => item.empleado))].length;
    const cargosUnicos = [...new Set(datos.map(item => item.cargo).filter(Boolean))].length;
    const areasUnicas = [...new Set(datos.map(item => item.areaTrabjao).filter(Boolean))].length;
    const proyectosUnicos = [...new Set(datos.map(item => item.proyecto).filter(Boolean))].length;
    
    const estadosResumen = datos.reduce((acc, item) => {
      acc[item.estado] = (acc[item.estado] || 0) + 1;
      return acc;
    }, {});
    
    const horasPorEstado = datos.reduce((acc, item) => {
      acc[item.estado] = (acc[item.estado] || 0) + item.horasProximoMes;
      return acc;
    }, {});
    
    return {
      totalRegistros,
      totalHoras: Math.round(totalHoras * 100) / 100,
      promedioHorasPorEmpleado: Math.round((totalHoras / empleadosUnicos) * 100) / 100,
      empleadosUnicos,
      cargosUnicos,
      areasUnicas,
      proyectosUnicos,
      estadosResumen,
      horasPorEstado,
      fechaGeneracion: new Date().toISOString()
    };
  }

  /**
   * 📤 Exportar datos procesados a Excel
   */
  exportarAExcel(datos, nombreArchivo = 'compensaciones_importadas') {
    try {
      const datosParaExcel = datos.map(item => ({
        'Empleado': item.empleado,
        'Cargo': item.cargo,
        'Área de Trabajo': item.areaTrabjao,
        'Proyecto': item.proyecto,
        'Horas Próximo Mes': item.horasProximoMes,
        'Estado': item.estado,
        'Días Laborables': item.diasLaborables,
        'Minutos Acumulados': item.minutosAcumulados,
        'Horas Calculadas': item.horasCalculadas,
        'Tareos Procesados': item.tareosProcesados,
        'Fila Original': item.numeroFila
      }));
      
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(datosParaExcel);
      
      // Ajustar ancho de columnas
      const columnasAncho = [
        { wch: 25 }, // Empleado
        { wch: 20 }, // Cargo
        { wch: 18 }, // Área
        { wch: 15 }, // Proyecto
        { wch: 12 }, // Horas
        { wch: 10 }, // Estado
        { wch: 12 }, // Días
        { wch: 15 }, // Minutos
        { wch: 12 }, // Horas Calc
        { wch: 12 }, // Tareos
        { wch: 10 }  // Fila
      ];
      worksheet['!cols'] = columnasAncho;
      
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Compensaciones');
      
      const fechaActual = new Date().toISOString().split('T')[0];
      const nombreFinal = `${nombreArchivo}_${fechaActual}.xlsx`;
      
      XLSX.writeFile(workbook, nombreFinal);
      
      return {
        success: true,
        archivo: nombreFinal,
        registros: datos.length
      };
      
    } catch (error) {
      console.error('Error exportando a Excel:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * 🎯 Filtrar datos por criterios
   */
  filtrarDatos(datos, filtros = {}) {
    let datosFiltrados = [...datos];
    
    if (filtros.empleado) {
      datosFiltrados = datosFiltrados.filter(item => 
        item.empleado.toLowerCase().includes(filtros.empleado.toLowerCase())
      );
    }
    
    if (filtros.cargo) {
      datosFiltrados = datosFiltrados.filter(item => 
        item.cargo.toLowerCase().includes(filtros.cargo.toLowerCase())
      );
    }
    
    if (filtros.area) {
      datosFiltrados = datosFiltrados.filter(item => 
        item.areaTrabjao.toLowerCase().includes(filtros.area.toLowerCase())
      );
    }
    
    if (filtros.proyecto) {
      datosFiltrados = datosFiltrados.filter(item => 
        item.proyecto.toLowerCase().includes(filtros.proyecto.toLowerCase())
      );
    }
    
    if (filtros.estado) {
      datosFiltrados = datosFiltrados.filter(item => 
        item.estado === filtros.estado
      );
    }
    
    if (filtros.horasMinimas !== undefined) {
      datosFiltrados = datosFiltrados.filter(item => 
        item.horasProximoMes >= filtros.horasMinimas
      );
    }
    
    if (filtros.horasMaximas !== undefined) {
      datosFiltrados = datosFiltrados.filter(item => 
        item.horasProximoMes <= filtros.horasMaximas
      );
    }
    
    return datosFiltrados;
  }
}

export default new ImportacionCompensacionesService();
